import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import { CheckPicker, Panel, PanelGroup, Badge, Divider } from "rsuite";
import { colors } from "../../../../ui";

const DataPointSelector = ({
  availableMetrics = [],
  selectedMetrics = [],
  onSelectionChange,
  manufacturer = "",
  rawData,
  className,
  ...props
}) => {
  const [expandedPanels, setExpandedPanels] = useState(["continuous"]);

  // Group metrics by type (continuous vs discrete)
  const groupedMetrics = useMemo(() => {
    const groups = {
      continuous: [],
      discrete: [],
      combined: [],
    };

    availableMetrics.forEach((metric) => {
      const [fields, type, enabled] = metric;
      if (!enabled) return;

      const fieldName = fields[0];
      // Try to get the actual label from the raw data filterConfig
      let displayLabel = fieldName;
      if (rawData?.filterConfig) {
        const filterEntry = rawData.filterConfig.find(
          ([name]) => name === fieldName,
        );
        if (filterEntry) {
          displayLabel = filterEntry[1]; // Use the actual label from filterConfig
        }
      }

      const metricData = {
        value: fieldName, // Use the actual field name as identifier
        label: displayLabel,
        fields: fields,
        type: type,
        category: getMetricCategory(fieldName),
        description: getMetricDescription(fieldName, manufacturer),
      };

      if (fields.length > 1) {
        groups.combined.push(metricData);
      } else {
        groups[type].push(metricData);
      }
    });

    return groups;
  }, [availableMetrics, manufacturer, rawData?.filterConfig]);

  // Use grouped metrics directly (no search filtering)
  const filteredGroups = groupedMetrics;

  // Convert selected metrics to CheckPicker format
  const getSelectedValues = (groupKey) => {
    return selectedMetrics.filter((metric) => {
      const groupMetrics = groupedMetrics[groupKey] || [];
      return groupMetrics.some((gm) => gm.value === metric);
    });
  };

  // Handle metric selection
  const handleSelectionChange = (groupKey, values) => {
    // Remove all metrics from this group from current selection
    const otherGroupMetrics = selectedMetrics.filter((metric) => {
      const groupMetrics = groupedMetrics[groupKey] || [];
      return !groupMetrics.some((gm) => gm.value === metric);
    });

    // Add new selection from this group
    const newSelection = [...otherGroupMetrics, ...values];
    onSelectionChange(newSelection);
  };

  // Get color for metric type
  const getMetricColor = (type) => {
    switch (type) {
      case "continuous":
        return colors.primary.main;
      case "discrete":
        return colors.warning.main;
      case "combined":
        return colors.success.main;
      default:
        return colors.neutral[500];
    }
  };

  const renderMetricGroup = (groupKey, title, icon) => {
    const metrics = filteredGroups[groupKey] || [];
    if (metrics.length === 0) return null;

    return (
      <Panel
        key={groupKey}
        header={
          <PanelHeader>
            {icon}
            <span>{title}</span>
            <Badge
              color={getMetricColor(
                groupKey === "combined" ? "continuous" : groupKey,
              )}
              content={metrics.length}
            />
          </PanelHeader>
        }
        id={groupKey}
        expanded={expandedPanels.includes(groupKey)}
        onEnter={() => setExpandedPanels([...expandedPanels, groupKey])}
        onExit={() =>
          setExpandedPanels(expandedPanels.filter((p) => p !== groupKey))
        }
      >
        <MetricCheckPicker
          data={metrics}
          value={getSelectedValues(groupKey)}
          onChange={(values) => handleSelectionChange(groupKey, values)}
          valueKey="value"
          labelKey="label"
          searchable={false}
          placeholder={`Select ${title.toLowerCase()} metrics...`}
          renderMenuItem={(label, item) => (
            <MenuItemContent>
              <MetricLabel>
                <MetricName>{item.label}</MetricName>
                <MetricType color={getMetricColor(item.type)}>
                  {item.type}
                </MetricType>
              </MetricLabel>
              <MetricDescription>{item.description}</MetricDescription>
            </MenuItemContent>
          )}
          renderValue={(value, items) => (
            <ValueContainer>
              {items.map((item) => (
                <MetricChip key={item.value} color={getMetricColor(item.type)}>
                  {item.label}
                </MetricChip>
              ))}
            </ValueContainer>
          )}
        />
      </Panel>
    );
  };

  return (
    <SelectorContainer className={className} {...props}>
      <HeaderSection>
        <SectionTitle>Select Data Points to Chart</SectionTitle>
      </HeaderSection>

      <Divider />

      <MetricGroupsContainer>
        <StyledPanelGroup accordion={false}>
          {renderMetricGroup("continuous", "Continuous Metrics", "📈")}
          {renderMetricGroup("discrete", "Discrete Metrics", "🔘")}
          {renderMetricGroup("combined", "Multi-Series Metrics", "📊")}
        </StyledPanelGroup>
      </MetricGroupsContainer>

      {selectedMetrics.length > 0 && (
        <>
          <Divider />
          <SummarySection>
            <SummaryTitle>
              Selected Metrics ({selectedMetrics.length})
            </SummaryTitle>
            <SelectedMetricsContainer>
              {selectedMetrics.map((metricValue) => {
                const metric = Object.values(groupedMetrics)
                  .flat()
                  .find((m) => m.value === metricValue);

                if (!metric) return null;

                return (
                  <SelectedMetricChip
                    key={metric.value}
                    color={getMetricColor(metric.type)}
                  >
                    {metric.label}
                    <RemoveButton
                      onClick={() => {
                        const newSelection = selectedMetrics.filter(
                          (m) => m !== metricValue,
                        );
                        onSelectionChange(newSelection);
                      }}
                    >
                      ×
                    </RemoveButton>
                  </SelectedMetricChip>
                );
              })}
            </SelectedMetricsContainer>
          </SummarySection>
        </>
      )}
    </SelectorContainer>
  );
};

// Helper functions
const getMetricCategory = (fieldName) => {
  const categories = {
    pressure: ["Pressure", "HEPressure", "SCPressure", "DiffPressureSwitch"],
    temperature: ["Temp", "Shield", "ColdHead", "WaterTemp", "RoomTemp"],
    level: ["Level", "LHePct", "HeliumLevel", "HELevel"],
    flow: ["Flow", "BoilOffRate"],
    power: ["Power", "Voltage", "Battery"],
    status: ["Status", "Alarm", "Quench", "Cerr"],
  };

  for (const [category, keywords] of Object.entries(categories)) {
    if (keywords.some((keyword) => fieldName.includes(keyword))) {
      return category;
    }
  }
  return "other";
};

const getMetricDescription = (fieldName, manufacturer) => {
  const descriptions = {
    LHePct: "Liquid Helium Percentage Level",
    Pressure: "System Pressure Reading",
    CompressorStatus: "Compressor Operational Status",
    TechRoomTemp: "Technical Room Temperature",
    BoilOffRate: "Helium Boil-off Rate",
    HELevel: "Helium Level Sensor",
    Shield: "Shield Temperature",
    WaterFlow: "Cooling Water Flow Rate",
    BatteryV: "Battery Voltage Level",
  };

  return (
    descriptions[fieldName] ||
    `${fieldName} measurement for ${manufacturer} system`
  );
};

// Styled Components
const SelectorContainer = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid ${colors.neutral[200]};
  overflow: hidden;
`;

const HeaderSection = styled.div`
  padding: 20px;
`;

const SectionTitle = styled.h3`
  margin: 0;
  color: ${colors.neutral[800]};
  font-size: 18px;
  font-weight: 600;
`;

const MetricGroupsContainer = styled.div`
  padding: 0 20px;
`;

const StyledPanelGroup = styled(PanelGroup)`
  .rs-panel {
    margin-bottom: 12px;
    border: 1px solid ${colors.neutral[200]};
    border-radius: 8px;
    overflow: hidden;
  }

  .rs-panel-header {
    background: ${colors.neutral[50]};
    padding: 12px 16px;
    border-bottom: 1px solid ${colors.neutral[200]};
  }

  .rs-panel-body {
    padding: 16px;
  }
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;

  span {
    flex: 1;
  }
`;

const MetricCheckPicker = styled(CheckPicker)`
  width: 100%;

  .rs-picker-toggle {
    min-height: 48px !important;
    border-radius: 8px !important;
  }
`;

const MenuItemContent = styled.div`
  padding: 4px 0;
`;

const MetricLabel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
`;

const MetricName = styled.span`
  font-weight: 500;
  color: ${colors.neutral[800]};
`;

const MetricType = styled.span`
  background: ${(props) => props.color};
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
`;

const MetricDescription = styled.div`
  font-size: 12px;
  color: ${colors.neutral[600]};
  font-style: italic;
`;

const ValueContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
`;

const MetricChip = styled.span`
  background: ${(props) => props.color}15;
  color: ${(props) => props.color};
  border: 1px solid ${(props) => props.color}30;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
`;

const SummarySection = styled.div`
  padding: 20px;
  background: ${colors.neutral[50]};
`;

const SummaryTitle = styled.h4`
  margin: 0 0 12px 0;
  color: ${colors.neutral[700]};
  font-size: 14px;
  font-weight: 600;
`;

const SelectedMetricsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const SelectedMetricChip = styled.div`
  background: ${(props) => props.color};
  color: white;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  padding: 0;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

export default DataPointSelector;
